import { DataTable } from '@components/DataTable';
import { FolderTreeGroupingCell } from '@modules/projects/components/TaskImportDrawer/FoldersStep/FolderTreeGroupingCell';
import { useCurrentProject } from '@modules/projects/utils/useCurrentProject';
import { fixHierarchyPaths } from '@modules/tasks/hierarchyHelpers';
import { Stack } from '@mui/material';
import { DataGridProProps, GridRowSelectionModel } from '@mui/x-data-grid-pro';
import { useProjectFoldersQuery } from 'gql/index';
import React, { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

interface Props {
  onFolderSelected: (documentId: number | null) => void;
}

export const FolderPicker: React.FC<Props> = ({ onFolderSelected }) => {
  const { projectId } = useCurrentProject();
  const { data: folders } = useProjectFoldersQuery({ projectId: projectId }, { select: p => p.projectFolders });
  const { formatMessage } = useIntl();

  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel | undefined>();
  const hierarchyFixedFolders = useMemo(() => fixHierarchyPaths(folders, f => f.path), [folders]);


  const groupingColDef: DataGridProProps<typeof hierarchyFixedFolders[0]>['groupingColDef'] = {
    headerName: formatMessage({ id: 'Name' }),
    flex: 1,
    renderCell: props => (
      <Stack sx={{ cursor: 'pointer' }} width={'100%'} height={'100%'} justifyContent={'center'}>
        <FolderTreeGroupingCell {...props} />
      </Stack>
    )
  };

  const getTreeDataPath = useCallback((row: typeof hierarchyFixedFolders[0]) => row.hierarchyPath.split('/').filter(Boolean), []);

  return (
    <DataTable<typeof hierarchyFixedFolders[0]>
      autoHeight
      columns={[]}
      rows={hierarchyFixedFolders}
      rowSelection
      getRowId={row => row.id}
      disableRowSelectionOnClick={false}
      rowSelectionModel={selectionModel}
      onRowSelectionModelChange={(model) => { setSelectionModel(model); onFolderSelected(Number(Array.from(model.ids)[0])) }}
      treeData
      groupingColDef={groupingColDef}
      defaultGroupingExpansionDepth={0}
      getTreeDataPath={getTreeDataPath}
      rowSelectionPropagation={{ parents: false, descendants: false }}
      noDataMessage={formatMessage({ id: 'No valid folders were found, or you do not have access to any folders.' })}
    />
  );

};